import React, { useState, useEffect, useRef } from 'react';

const syne  = { fontFamily: "'Syne', 'Inter', sans-serif" };
const inter = { fontFamily: "'Inter', sans-serif" };

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ─── data ───────────────────────────────────────────────────────────────────
const NICHES = [
  {
    key: "sport",   label: "Sport",   accent: "#ef4444",
    left:  { initials: "DS", name: "Darren Keir",  followers: "5,000+",  tag: "Athlete coach",     bg: ["#1a0d0d", "#ef4444"], image: require("./images/sports.jpg") },
    right: { initials: "AK", name: "Ava Kallin",    followers: "4,800+",  tag: "Running guide",     bg: ["#0f1929", "#f97316"], image: require("./images/fitness.jpg") },
  },
  {
    key: "music",   label: "Music",   accent: "#a855f7",
    left:  { initials: "AB", name: "Aria Blake",    followers: "9,000+",  tag: "Producer",          bg: ["#1a0d2e", "#8b5cf6"], image: require("./images/musicgirl.jpg") },
    right: { initials: "KW", name: "Kai Winters",   followers: "7,600+",  tag: "Vocal coach",       bg: ["#200d1a", "#a855f7"], image: require("./images/music.jpg") },
  },
  {
    key: "art",     label: "Art",     accent: "#f59e0b",
    left:  { initials: "OV", name: "Simon Fier",   followers: "12,000+",  tag: "Illustrator",       bg: ["#1a1200", "#f59e0b"], image: require("./images/art1.jpg") },
    right: { initials: "IS", name: "Isla Sun",      followers: "5,000+",  tag: "Digital painter",   bg: ["#0a1a10", "#22d3ee"], image: require("./images/art2.jpg") },
  },
  {
    key: "fashion", label: "Fashion", accent: "#ec4899",
    left:  { initials: "VL", name: "Vera Lux",      followers: "23,000+",  tag: "Stylist",           bg: ["#200d18", "#ec4899"], image: require("./images/fashion.jpg") },
    right: { initials: "BF", name: "Bella Forte",   followers: "15,000+",  tag: "Trend forecaster",  bg: ["#1a0d20", "#d946ef"], image: require("./images/cosplay.jpg") },
  },
  {
    key: "travel",  label: "Travel",  accent: "#10b981",
    left:  { initials: "LF", name: "Diego Cruz",      followers: "7,000+",  tag: "Solo traveller",    bg: ["#0d1a0d", "#10b981"], image: require("./images/travel1.jpg") },
    right: { initials: "DC", name: "Lena Fox",    followers: "32,000+",  tag: "City guides",       bg: ["#0d1420", "#0ea5e9"], image: require("./images/travel2.jpg") },
  },
];

// ─── parallax constants (px) ────────────────────────────────────────────────
const LEFT_START  = -70;
const LEFT_RANGE  = 160;
const RIGHT_START = 130;
const RIGHT_RANGE = 220;
const parallaxLeft  = (p) => LEFT_START  + p * LEFT_RANGE;
const parallaxRight = (p) => RIGHT_START - p * RIGHT_RANGE;

// ─── small SVG ──────────────────────────────────────────────────────────────
const Spark = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2 L13.8 8.2 L20 10 L13.8 11.8 L12 18 L10.2 11.8 L4 10 L10.2 8.2 Z" />
    <path d="M19 14 L19.7 16.3 L22 17 L19.7 17.7 L19 20 L18.3 17.7 L16 17 L18.3 16.3 Z" opacity=".75" />
  </svg>
);

// ─── photo card ─────────────────────────────────────────────────────────────
function PhotoCard({ initials, name, followers, tag, bg, accent, image }) {
  return (
    <div style={{
      width: "100%", height: "100%",
      borderRadius: 28,
      background: image ? "#0b0b0e" : `linear-gradient(155deg, ${bg[0]}, ${bg[1]})`,
      border: "1px solid rgba(255,255,255,0.08)",
      position: "relative", overflow: "hidden",
      boxShadow: "0 50px 100px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4)",
    }}>
      {image && (
        <img
          src={image}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center",
          }}
        />
      )}
      {/* film grain */}
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, opacity: 0.08, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1.4px)",
        backgroundSize: "3px 3px", mixBlendMode: "overlay",
      }} />
      {/* vignette */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.1) 45%, transparent 70%)",
        pointerEvents: "none", zIndex: 1,
      }} />
      {/* highlight glaze */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(160deg, rgba(255,255,255,0.16), transparent 40%)",
        pointerEvents: "none", zIndex: 1,
      }} />

      {/* big initials — fallback when no photo */}
      {!image && (
      <div style={{
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        ...syne, fontSize: "clamp(72px, 9vw, 140px)", fontWeight: 900,
        color: "rgba(255,255,255,0.45)",
        letterSpacing: "-0.04em",
      }}>{initials}</div>
      )}

      {/* top-right tag */}
      <div style={{
        position: "absolute", top: 18, right: 18, zIndex: 2,
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "5px 10px",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: 999,
        fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.9)",
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: accent, boxShadow: `0 0 8px ${accent}` }} />
        Top creator
      </div>

      {/* footer info */}
      <div style={{ position: "absolute", bottom: 22, left: 22, right: 22, zIndex: 2 }}>
        <div style={{ ...syne, fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em" }}>{name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12.5, color: "rgba(255,255,255,0.72)" }}>
          <span>{tag}</span>
          <span style={{ width: 3, height: 3, borderRadius: 999, background: "rgba(255,255,255,0.35)" }} />
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{followers}$ earned</span>
        </div>
      </div>
    </div>
  );
}

// ─── photo column ───────────────────────────────────────────────────────────
function PhotoColumn({ side, activeIdx, y, fadeColor = "#0b0b0e" }) {
  const photoSlotStyle = (isActive) => ({
    position: "absolute",
    top: "50%",
    left:  side === "left" ? "0" : "5%",
    right: side === "left" ? "5%" : "0",
    height: "62%",
    opacity: isActive ? 1 : 0,
    transition: "opacity 0.5s cubic-bezier(0.22,1,0.36,1)",
    pointerEvents: isActive ? "auto" : "none",
    transform: `translateY(calc(-50% + ${y}px))`,
    willChange: "transform, opacity",
  });
  return (
    <div className="ndrum-col" style={{ position: "relative", height: "100%" }}>
      {/* top/bottom fade masks */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "20%",
        background: `linear-gradient(to bottom, ${fadeColor}, transparent)`,
        zIndex: 5, pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "20%",
        background: `linear-gradient(to top, ${fadeColor}, transparent)`,
        zIndex: 5, pointerEvents: "none" }} />
      {NICHES.map((n, i) => (
        <div key={n.key} style={photoSlotStyle(i === activeIdx)}>
          <PhotoCard {...(side === "left" ? n.left : n.right)} accent={n.accent} />
        </div>
      ))}
    </div>
  );
}

// ─── centre drum ────────────────────────────────────────────────────────────
function CentreList({ activeIdx, accent }) {
  return (
    <div className="ndrum-centre">
      {/* tiny eyebrow */}
      <div className="ndrum-eyebrow">
        <Spark size={11} color={accent} />
        <span>Built for</span>
      </div>

      {/* drum */}
      <div className="ndrum-drum">
        {NICHES.map((n, i) => {
          const isActive = i === activeIdx;
          const dist = Math.abs(i - activeIdx);
          let size, color;
          if (isActive)        { size = "clamp(54px, 6.5vw, 96px)"; color = "#fff"; }
          else if (dist === 1) { size = "clamp(34px, 4.2vw, 56px)"; color = "rgba(255,255,255,0.22)"; }
          else                  { size = "clamp(24px, 3vw, 36px)";   color = "rgba(255,255,255,0.08)"; }
          return (
            <div key={n.key} style={{
              padding: "6px 0",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 14,
              transition: "all 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
            }}>
              {isActive && (
                <span style={{
                  width: 10, height: 10, borderRadius: 999,
                  background: accent,
                  boxShadow: `0 0 18px ${accent}`,
                  flex: "none",
                  animation: "ndrum-pulse 1.6s ease-in-out infinite",
                }} />
              )}
              <span style={{
                ...syne, fontWeight: 800, letterSpacing: "-0.025em",
                fontSize: size, color,
                lineHeight: 1.05,
                background: isActive ? `linear-gradient(180deg, #fff, ${accent}cc)` : "none",
                WebkitBackgroundClip: isActive ? "text" : "border-box",
                WebkitTextFillColor: isActive ? "transparent" : color,
                transition: "all 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
                whiteSpace: "nowrap",
              }}>{n.label}</span>
            </div>
          );
        })}
      </div>

      {/* bottom progress strip */}
      <div className="ndrum-progress">
        {NICHES.map((n, i) => (
          <div key={n.key} style={{
            width: i === activeIdx ? 32 : 18,
            height: 3, borderRadius: 999,
            background: i === activeIdx ? n.accent : "rgba(255,255,255,0.12)",
            boxShadow: i === activeIdx ? `0 0 12px ${n.accent}` : "none",
            transition: "all 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── pinned drum section ──────────────────────────────────────────────────────
function NicheDrumSection() {
  const outerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [progress,  setProgress]  = useState(0);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const update = () => {
      const rect = outer.getBoundingClientRect();
      const total = outer.offsetHeight - window.innerHeight;
      const raw = clamp(-rect.top / Math.max(1, total), 0, 1);
      setProgress(raw);
      setActiveIdx(Math.round(raw * (NICHES.length - 1)));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const leftY  = parallaxLeft(progress);
  const rightY = parallaxRight(progress);
  const accent = NICHES[activeIdx].accent;

  return (
    <section ref={outerRef} className="ndrum-section" aria-label="Premsi for every niche">
      <div className="ndrum-pin">
        {/* ambient backdrop tinted by active accent */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(40% 50% at 50% 50%, ${accent}22, transparent 70%), #0b0b0e`,
          transition: "background 0.6s cubic-bezier(.22,1,.36,1)",
          pointerEvents: "none",
        }} />

        {/* faint grid */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "100px 100px",
          maskImage: "radial-gradient(60% 70% at 50% 50%, #000, transparent 85%)",
          WebkitMaskImage: "radial-gradient(60% 70% at 50% 50%, #000, transparent 85%)",
          pointerEvents: "none",
        }} />

        {/* corner labels 
        <div className="ndrum-corner-left" style={{
          position: "absolute", top: 32, left: 40,
          display: "flex", alignItems: "center", gap: 10,
          fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "rgba(255,255,255,0.45)",
        }}>
          <span style={{ color: accent }}>0{activeIdx + 1}</span>
          <span style={{ width: 24, height: 1, background: "rgba(255,255,255,0.2)" }} />
          <span>/ 0{NICHES.length}</span>
          <span style={{ width: 24, height: 1, background: "rgba(255,255,255,0.2)" }} />
          <span>{NICHES[activeIdx].label}</span>
        </div>*/}
        {/* <div className="ndrum-corner-right" style={{
          position: "absolute", top: 32, right: 40,
          fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "rgba(255,255,255,0.45)",
        }}>
          Premsi · for every niche
        </div>*/}

        {/* main grid */}
        <div className="ndrum-grid">
          {/* mobile-only crossfading creator photo (hidden on desktop) */}
          <div className="ndrum-mobile-photo" aria-hidden="true">
            {NICHES.map((n, i) => (
              <div key={n.key} className="ndrum-mphoto-slot" style={{ opacity: i === activeIdx ? 1 : 0 }}>
                <PhotoCard {...n.left} accent={n.accent} />
              </div>
            ))}
          </div>
          <PhotoColumn side="left"  activeIdx={activeIdx} y={leftY}  />
          <CentreList  activeIdx={activeIdx} accent={accent} />
          <PhotoColumn side="right" activeIdx={activeIdx} y={rightY} />
        </div>

        {/* bottom centre helper */}
        <div style={{
          position: "absolute", bottom: 28, left: "50%", transform: "translateX(-50%)",
          fontSize: 11, color: "rgba(255,255,255,0.32)",
          fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          Keep scrolling
        </div>
      </div>
    </section>
  );
}

// ─── filler spacer ────────────────────────────────────────────────────────────
function Spacer({ children, hint }) {
  return (
    <section className="ndrum-spacer">
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0,
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
        maskImage: "radial-gradient(70% 70% at 50% 50%, #000, transparent 80%)",
        WebkitMaskImage: "radial-gradient(70% 70% at 50% 50%, #000, transparent 80%)",
        pointerEvents: "none",
      }} />
      <div style={{ textAlign: "center", maxWidth: 1000, position: "relative" }}>
        {children}
      </div>
      {hint && (
        <div style={{
          position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          color: "rgba(255,255,255,0.35)", fontSize: 12, fontWeight: 500,
          letterSpacing: "0.1em", textTransform: "uppercase",
        }}>
          <span>{hint}</span>
          <span className="ndrum-bounce" style={{ display: "inline-block" }}>↓</span>
        </div>
      )}
    </section>
  );
}

// ─── full section: hero spacer + pinned drum + CTA spacer ─────────────────────
export default function AltNicheDrum() {
  return (
    <div className="ndrum-root">
      <Spacer hint="Scroll to see our featured creators">
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "7px 14px 6px",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 999, color: "rgba(255,255,255,0.85)",
          fontSize: 11.5, fontWeight: 700,
          letterSpacing: "0.18em", textTransform: "uppercase",
        }}>
          <Spark size={11} color="#fff" />
          <span>One platform · every creator</span>
        </div>
        <h1 style={{
          ...syne,
          margin: "30px auto 0",
          fontSize: "clamp(56px, 8.4vw, 132px)",
          lineHeight: 0.94,
          letterSpacing: "-0.04em",
          fontWeight: 900,
          maxWidth: 1200,
          color: "#fff",
        }}>
          Whatever you create,{" "}
          <span style={{
            background: "linear-gradient(90deg, #ef4444, #a855f7 35%, #f59e0b 60%, #ec4899 80%, #10b981)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>Premsi runs on it.</span>
        </h1>
        
      </Spacer>

      <NicheDrumSection />

      <Spacer>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "rgba(255,255,255,0.45)",
        }}>Your turn</div>
        <h2 style={{
          ...syne,
          margin: "20px auto 0",
          fontSize: "clamp(44px, 6vw, 92px)",
          lineHeight: 1,
          letterSpacing: "-0.03em",
          fontWeight: 900,
          maxWidth: 1100,
          color: "#fff",
        }}>
          Your niche is{" "}
          <span style={{
            background: "linear-gradient(180deg, #ffffff, #b8b8c8)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>next.</span>
        </h2>
        <p style={{
          ...inter,
          maxWidth: 620, margin: "26px auto 36px",
          fontSize: 18, lineHeight: 1.55, color: "rgba(255,255,255,0.6)",
        }}>
          Whatever you teach, sell, perform, or share — the toolkit is the same.
          Five minutes to set up. Ninety percent to keep. The newest AI tools at your fingertips.
        </p>
        <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <a href="/invitation" style={{
            padding: "14px 22px", borderRadius: 999,
            background: "linear-gradient(180deg, #fff, #e6e6ea)",
            color: "#0a0a0c", fontWeight: 700, textDecoration: "none",
            boxShadow: "0 12px 30px rgba(255,255,255,0.18)",
            fontSize: 15,
          }}>Start creating</a>
         {/* <a href="/" style={{
            padding: "14px 22px", borderRadius: 999,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#fff", fontWeight: 600, textDecoration: "none",
            fontSize: 15,
          }}>See creator stories</a>*/}
        </div>
      </Spacer>
    </div>
  );
}
